import { Fragment, useState } from "react";
import { Box, Divider, Flex, Text } from "@chakra-ui/react";

import { TVLDataType } from "hooks/useTVL";
import { formatNumber } from "utils";

import AuditsModal from "@components/AuditsMoal";
import BaseImage from "@components/BaseImage";
import { UseQueryResult } from "@tanstack/react-query";
import Tooltip from "@components/Tooltip";
import { ApyResponse } from "services";

interface CenterHeaderProps {
  tvl: TVLDataType;
  restakeApy: UseQueryResult<ApyResponse, Error>;
  heading: string;
}

const CenterHeader = ({ tvl, restakeApy, heading }: CenterHeaderProps) => {
  const [isAuditsModalOpen, setIsAuditsModalOpen] = useState(false);

  let eigenAPY = restakeApy?.data?.eigenAPY;

  if (!eigenAPY || eigenAPY <= 0) {
    eigenAPY = 0.7;
  }

  return (
    <>
      <Flex
        direction={"column"}
        align={"stretch"}
        justifyContent="space-between"
        p={{ base: "16px 0", md: 0 }}
        width={"100%"}
        gap={"16px"}
      >
        <Flex flexDir="row" gap="12px" alignItems={"center"}>
          <Text
            fontSize={{ base: "18px", md: "32px" }}
            lineHeight={{ base: "20px", md: "38px" }}
            fontWeight="600"
            color="textPrimary"
          >
            {heading}
          </Text>

          <Flex
            p={{ base: "2px 4px", md: "4px 6px" }}
            gap="4px"
            alignItems="center"
            borderRadius="8px"
            cursor="pointer"
            border="1px solid"
            borderColor="backgroundDivider"
            height="32px"
            onClick={() => setIsAuditsModalOpen(true)}
            w="fit-content"
          >
            <BaseImage
              height={16}
              width={16}
              src="/assets/verified_user.svg"
              alt="verified"
            />

            <Text
              color="textPrimary"
              fontWeight="400"
              fontSize="12px"
              lineHeight="20px"
            >
              Audited
            </Text>
          </Flex>
        </Flex>

        <Flex
          direction={"column"}
          gap={"8px"}
          fontSize={{ base: "12px", md: "14px" }}
          fontWeight="600"
        >
          <Flex justifyContent={"space-between"}>
            <Flex alignItems={"center"} gap="4px">
              <Text>APY</Text>
              <Tooltip label={<Box>30-day moving average </Box>} />
            </Flex>
            <Flex alignItems={"center"} gap="4px">
              <Text>
                {restakeApy.data
                  ? `${(
                      +restakeApy.data.kelpAPY.toFixed(1) + +eigenAPY
                    ).toFixed(1)}%`
                  : "-"}
              </Text>
              <Tooltip
                placement="top"
                label={
                  <Flex flexDir="column" gap="4px" w="120px">
                    <Flex items-center justify="space-between">
                      <Text>Staking APR</Text>
                      <Text>
                        {restakeApy.data
                          ? `${restakeApy.data.kelpAPY.toFixed(1)}%`
                          : "-"}
                      </Text>
                    </Flex>
                    <Flex items-center justify="space-between">
                      <Text>Restaking APR</Text>
                      {restakeApy.data ? `${eigenAPY.toFixed(1)}%` : "-"}
                    </Flex>
                  </Flex>
                }
              />
            </Flex>
          </Flex>
          <Flex justifyContent={"space-between"}>
            <Flex alignItems={"center"} gap="4px">
              <Text>Total Value Locked</Text>
            </Flex>
            <Fragment>
              <Flex gap="6px" alignItems="center" justifyContent="center">
                <Flex gap="4px" alignItems="center" justifyContent="center">
                  <Text>
                    {tvl.tvl?.value
                      ? formatNumber(tvl.tvl.value, false, 2)
                      : "-"}
                  </Text>
                  <Text>ETH</Text>
                </Flex>
                <Divider
                  orientation="vertical"
                  borderColor="textTertiary"
                  borderWidth="1px"
                  height="8px"
                />
                <Text fontWeight={"500"}>
                  {tvl.tvl?.value ? `$${formatNumber(tvl.tvl.usdTvl)}` : "-"}
                </Text>
              </Flex>
            </Fragment>
          </Flex>
        </Flex>
      </Flex>

      <AuditsModal
        isOpen={isAuditsModalOpen}
        closeModal={() => setIsAuditsModalOpen(false)}
      />
    </>
  );
};

export default CenterHeader;
